/**
 * ASKING THE DESK WHERE SOMEWHERE IS.
 *
 * The thin half of the address lookup. The thinking is in
 * `server/routes/address.ts`; this is the browser's side of it and it is
 * deliberately almost nothing: one fetch, one shape, and a refusal to
 * throw.
 *
 * NEVER THROWS. An address box that stops accepting typing because a
 * geocoder had a bad minute is worse than one that never offered to help.
 * Every failure returns an empty list, the field stays an ordinary text
 * input, and the person types the address the way they always did.
 */

interface Suggestion {
  line: string; city: string; state: string; zip: string;
  lat: number; lng: number;
}

/**
 * The lines to offer for what has been typed so far.
 *
 * Returns the whole one-line address, because that string is what gets
 * saved — the map's reader wants "street, town, ST zip" and this is the
 * only place in the product that can guarantee it gets one.
 */
export async function suggestAddress(typed: string): Promise<string[]> {
  const q = typed.trim();
  if (q.length < 3) return [];
  try {
    const r = await fetch(`/api/address-suggest?q=${encodeURIComponent(q)}`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!r.ok) return [];
    const body = await r.json() as { suggestions?: Suggestion[] };
    return (body.suggestions ?? []).map((s) => s.line).filter(Boolean);
  } catch {
    return [];
  }
}
